import { useEffect, useRef, useState } from 'react';

const TURNSTILE_SCRIPT_ID = 'cf-turnstile-script';
const TURNSTILE_SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

/**
 * Load Cloudflare Turnstile script once.
 * @returns {Promise<void>}
 */
function loadTurnstileScript() {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }

  if (window.turnstile) {
    return Promise.resolve();
  }

  const existing = document.getElementById(TURNSTILE_SCRIPT_ID);
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener(
        'error',
        () => reject(new Error('Turnstile failed to load')),
        {
          once: true,
        },
      );
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = TURNSTILE_SCRIPT_ID;
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Turnstile failed to load'));
    document.head.appendChild(script);
  });
}

/**
 * Cloudflare Turnstile widget for the marketing contact form.
 *
 * @param {object} props
 * @param {string} props.siteKey
 * @param {string} [props.language]
 * @param {(token: string) => void} props.onSuccess
 * @param {() => void} [props.onExpire]
 */
export function TurnstileWidget({ siteKey, language = 'en', onSuccess, onExpire }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onSuccessRef = useRef(onSuccess);
  const onExpireRef = useRef(onExpire);
  const [loadError, setLoadError] = useState(null);

  onSuccessRef.current = onSuccess;
  onExpireRef.current = onExpire;

  useEffect(() => {
    if (!siteKey || !containerRef.current) {
      return undefined;
    }

    let cancelled = false;

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) {
          return;
        }

        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          language,
          callback: (token) => onSuccessRef.current(token),
          'expired-callback': () => {
            onSuccessRef.current('');
            onExpireRef.current?.();
          },
          'error-callback': () => {
            onSuccessRef.current('');
            setLoadError('Verification failed. Please try again.');
          },
        });
      })
      .catch(() => {
        if (!cancelled) {
          setLoadError('Verification could not load. Please refresh and try again.');
        }
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current != null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, language]);

  if (!siteKey) {
    return null;
  }

  return (
    <div>
      <div ref={containerRef} />
      {loadError && (
        <p
          style={{
            color: 'var(--mantine-color-red-6)',
            fontSize: '0.875rem',
            marginTop: '0.5rem',
          }}
        >
          {loadError}
        </p>
      )}
    </div>
  );
}
